import React, { useState, useRef } from 'react';
import { Upload, Plane, FileSpreadsheet, Check } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000';

export default function AirlineDataUploader() {
  const [selectedAirline, setSelectedAirline] = useState('Select an airline');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const airlines = [
    'Select an airline',
    'Akasa',
    'Air India Express',
    'IndiGo',
    'SpiceJet',
  ];

  const handleFileUpload = (file) => {
    if (file && (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                 file.type === 'application/vnd.ms-excel' ||
                 file.name.endsWith('.xlsx') || 
                 file.name.endsWith('.xls'))) {
      setUploadedFile(file);
      setUploadResult(null);
    } else {
      alert('Please upload a valid Excel file (.xlsx or .xls)');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAirline || selectedAirline === 'Select an airline') {
      alert('Please select an airline');
      return;
    }
    if (!uploadedFile) {
      alert('Please upload an Excel file');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('airline', selectedAirline);

      const response = await axios.post(`${API_BASE}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult({
        success: true,
        taskId: response.data.task_id,
        airline: selectedAirline,
        filename: uploadedFile.name
      });

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadResult({
        success: false,
        error: error.response?.data?.error || error.message
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetFile = () => {
    setUploadedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setSelectedAirline('Select an airline');
    setUploadedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-center p-6 min-h-screen">
      <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-4">
            <Plane className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Airline Data Hub</h1>
          <p className="text-gray-600">Upload your Excel data seamlessly</p>
        </div>

        {!uploadResult && (
          <>
            {/* Airline Dropdown */}
            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-3 text-lg">
                Choose Airline
              </label>
              <div className="relative">
                <select
                  value={selectedAirline}
                  onChange={(e) => setSelectedAirline(e.target.value)}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl text-gray-700 bg-white cursor-pointer transition-all duration-300 hover:border-blue-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 focus:outline-none appearance-none text-base"
                >
                  {airlines.map((airline, index) => (
                    <option key={index} value={airline} disabled={index === 0}>
                      {airline}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* File Upload Area */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-gray-700 font-semibold text-lg">
                  Upload Excel File
                </label>
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API_BASE}/download-template`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'Format.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error('Template download failed:', error);
                      alert('Template download failed');
                    }
                  }}
                  className="bg-green-500 text-white font-medium py-2 px-4 rounded-lg hover:bg-green-600 transition-all duration-300 text-sm"
                >
                  📄 Download Template
                </button>
              </div>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer ${
                  isDragOver
                    ? 'border-blue-500 bg-blue-50/50'
                    : uploadedFile
                    ? 'border-green-400 bg-green-50/50'
                    : 'border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/30'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                
                {uploadedFile ? (
                  <div className="space-y-3">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-700">{uploadedFile.name}</p>
                      <p className="text-sm text-green-600">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        resetFile();
                      }}
                      className="text-sm text-red-500 hover:text-red-700 underline"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                      <Upload className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700">Drop your Excel file here</p>
                      <p className="text-sm text-gray-500">or click to browse</p>
                    </div>
                    <div className="flex items-center justify-center space-x-2 text-xs text-gray-400">
                      <FileSpreadsheet className="w-4 h-4" />
                      <span>Supports .xlsx and .xls files</span>
                    </div>
                  </div>
                )}
              </div>
            </div>



            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={isUploading}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-4 px-6 rounded-xl hover:from-blue-600 hover:to-purple-700 transform hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isUploading ? 'Processing...' : 'Submit Data'}
            </button>

            {/* Status indicator */}
            {selectedAirline && selectedAirline !== 'Select an airline' && uploadedFile && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 text-center">
                  ✓ Ready to submit - {selectedAirline} data prepared
                </p>
              </div>
            )}
          </>
        )}

        {/* Success/Error Result */}
        {uploadResult && (
          <div className="space-y-4">
            {uploadResult.success ? (
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-green-700 mb-2">Upload Successful!</h2>
                  <p className="text-gray-600 mb-4">
                    Your {uploadResult.airline} data has been submitted for processing.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-700">
                      <strong>Task ID:</strong> {uploadResult.taskId}
                    </p>
                    <p className="text-sm text-blue-700">
                      <strong>File:</strong> {uploadResult.filename}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500">
                    You can track the processing status in the main dashboard.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-red-700 mb-2">Upload Failed</h2>
                  <p className="text-gray-600 mb-4">
                    There was an error processing your request.
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-700">
                      <strong>Error:</strong> {uploadResult.error}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={resetForm}
              className="w-full bg-gray-500 text-white font-semibold py-3 px-6 rounded-xl hover:bg-gray-600 transition-all duration-300"
            >
              Upload Another File
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}